import React, { useState } from 'react';
import { Users, Send, DollarSign, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { ReferralModal } from './ReferralModal';

const STEPS = [
  { icon: Users, fr: ['Trouvez un ami', 'Quelqu’un de votre entourage a besoin d’un véhicule et d’un bon prêt auto.'], en: ['Find a friend', 'Someone you know needs a vehicle and a good car loan.'] },
  { icon: Send, fr: ['Envoyez-le chez nous', 'Remplissez le formulaire avec ses coordonnées et les vôtres, ou donnez-lui votre nom.'], en: ['Send them our way', 'Fill out the form with their info and yours, or give them your name.'] },
  { icon: DollarSign, fr: ['Recevez 350 $ comptant', 'Dès que votre ami devient notre client, on vous remet 350 $ comptant.'], en: ['Get $350 cash', 'As soon as your friend becomes our client, we hand you $350 cash.'] },
];

export const ReferralSection = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const [open, setOpen] = useState(false);

  return (
    <section className="relative overflow-hidden bg-[#0b130e] border-t-4 border-[#4ade80] py-20 md:py-24" data-testid="referral-section">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#1a5c3b] rounded-full blur-3xl opacity-40 animate-blob pointer-events-none"></div>
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="inline-block bg-white text-[#0b130e] text-xs font-bold uppercase tracking-[0.25em] px-4 py-1.5 mb-6" style={{ clipPath: 'polygon(0 0, 94% 0, 100% 100%, 0 100%)' }}>
            {en ? 'Refer a friend' : 'Référez un ami'}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6">
            {en ? 'Earn ' : 'Gagnez '}<span className="text-[#4ade80]">350 $</span><br />{en ? 'easily' : 'facilement'}
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed max-w-lg">
            {en
              ? <>Everyone knows someone who wants a new vehicle. Send them to us — if they become our client and give us your name, <strong className="text-white">we hand you $350 cash</strong> to thank you for <strong className="text-white">your support</strong>.</>
              : <>Tout le monde connaît quelqu’un qui aimerait changer de véhicule. Envoyez-le-nous — si cette personne devient notre client et nous donne votre nom, <strong className="text-white">on vous remet aussitôt 350 $ comptant</strong> pour vous remercier de <strong className="text-white">votre soutien</strong>.</>}
          </p>
        </div>

        <div>
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-8">{en ? 'How does it work?' : 'Comment ça marche ?'}</h3>
          <div className="space-y-6">
            {STEPS.map(({ icon: Icon, fr, en: e }, i) => {
              const [title, desc] = en ? e : fr;
              return (
                <div key={i} className="group flex items-start gap-5 animate-fade-up" style={{ animationDelay: `${i * 0.15}s` }} data-testid={`referral-step-${i + 1}`}>
                  <div className="w-14 h-14 shrink-0 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center transition-[transform,background-color] duration-300 group-hover:scale-110 group-hover:bg-[#1a5c3b]">
                    <Icon className="w-6 h-6 text-[#4ade80] group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-white">{title}</p>
                    <p className="text-gray-400">{desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={() => setOpen(true)} className="group btn-shimmer mt-10 inline-flex items-center gap-2 bg-white text-[#0b130e] font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-full transition-transform hover:scale-105" data-testid="referral-open-btn">
            {en ? 'Refer a friend' : 'Référer un ami'}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
      <ReferralModal open={open} onClose={() => setOpen(false)} language={language} />
    </section>
  );
};
